import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { Profile, PortfolioItem } from '@/hooks/useProfile';
import { Json } from '@/integrations/supabase/types';

export const useProfileSubmit = (
  user: User,
  profile: Profile,
  onProfileUpdate: (updatedProfile: Profile) => void,
  uploadAvatar: () => Promise<string | null>,
  setAvatarFile: (file: File | null) => void,
  targetUserId?: string
) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  // Validation function
  const validateForm = (formData: Profile): string | null => {
    if (!formData.username) {
      return "Full name is required";
    }

    if (!formData.profession) {
      return "Discipline is required";
    }

    return null; // No errors
  };

  const preparePortfolioItems = (items: PortfolioItem[] | null): any[] => {
    if (!items || !Array.isArray(items)) return [];
    
    return items.map(item => {
      // Create a clean representation for database storage
      const cleanItem = {
        id: item.id,
        title: item.title,
        description: item.description,
        image_url: item.image_url || '',
        created_at: item.created_at || new Date().toISOString(),
        updated_at: item.updated_at || new Date().toISOString(),
        is_featured: item.is_featured || false,
        status: item.status || 'published',
        website_url: item.website_url || '',
        order: typeof item.order === 'number' ? item.order : 0,
        // Handle images array if it exists
        images: Array.isArray(item.images) 
          ? item.images.map(img => ({
              id: img.id,
              url: img.url,
              order: typeof img.order === 'number' ? img.order : 0,
              caption: img.caption || '',
              isLegacy: img.isLegacy || false
            }))
          : []
      };
      
      return cleanItem;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    try {
      setIsUpdating(true);
      
      // First upload the avatar if there is one
      console.log('Starting profile update process');
      
      // Upload avatar and get the new URL
      const newAvatarUrl = await uploadAvatar();
      console.log('Avatar upload completed, result URL:', newAvatarUrl);
      
      // Don't use data URLs in the database, they're too large
      // If we got a data URL back, it means storage access failed, so keep the old URL
      const avatarUrl = newAvatarUrl && !newAvatarUrl.startsWith('data:') 
        ? newAvatarUrl 
        : profile.avatar_url;
      
      // Use targetUserId if provided (admin editing), otherwise use current user's ID
      const effectiveUserId = targetUserId || user.id;
      console.log('Effective User ID for profile update:', effectiveUserId);
      console.log('Final avatar URL for profile update:', avatarUrl);
      
      // Ensure arrays are properly initialized before sending to Supabase
      const skills = Array.isArray(profile.skills) ? profile.skills.filter(Boolean) : [];
      const languages = Array.isArray(profile.languages) ? profile.languages.filter(Boolean) : [];
      
      // Properly process portfolio items for Supabase
      const portfolioItemsJson = preparePortfolioItems(profile.portfolio_items);
      console.log('Portfolio items being saved:', portfolioItemsJson);
      
      const experienceItemsJson = Array.isArray(profile.experience_items)
        ? profile.experience_items.map(item => ({
            id: item.id,
            position: item.position,
            company: item.company, 
            start_date: item.start_date,
            end_date: item.end_date,
            current: item.current || false,
            description: item.description,
            location: item.location
          }))
        : [];
      
      // Update profile in database
      const { data, error } = await supabase
        .from('profiles')
        .update({
          username: profile.username,
          location: profile.location,
          profession: profile.profession,
          avatar_url: avatarUrl,
          bio: profile.bio,
          years_experience: profile.years_experience,
          skills: skills,
          education: profile.education,
          languages: languages,
          portfolio_url: profile.portfolio_url,
          instagram_username: profile.instagram_username,
          linkedin_username: profile.linkedin_username,
          portfolio_items: portfolioItemsJson as Json,
          experience_items: experienceItemsJson as Json,
          updated_at: new Date().toISOString(),
        })
        .eq('id', effectiveUserId)
        .select();
      
      if (error) {
        console.error('Profile update error from Supabase:', error);
        throw error;
      }
      
      console.log('Profile update successful:', data);
      
      // Create a new profile object with the updated avatar URL
      const updatedProfile = {
        ...profile,
        avatar_url: avatarUrl
      };
      
      // Clear the avatar file after successful upload
      setAvatarFile(null);
      
      // Update the profile state with the updated profile
      onProfileUpdate(updatedProfile);
      
      toast({
        title: "Profile updated",
        description: "The profile has been successfully updated.",
      });
    } catch (error: any) {
      console.error('Profile update error:', error);
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    isUpdating,
    validateForm,
    handleSubmit
  };
};
